/**
 * Step 5: Retirement Spending Plan
 * Plan spending needs across 1-5 retirement phases (#1538) plus one-time and recurring expenses
 */

import { useForm, useFieldArray } from 'react-hook-form';
import { useEffect, useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { Step6FormData, Step6Schema, SpendingPhase, type AllStepFormData, type OneTimeExpense, type RecurringExpense } from '../../types/wizard';
import { planYearToCalendar } from '../../utils/formatters';
import { wizardFormKeyDown } from './wizardFormKeyDown';
import { TourButton } from '../TourGuide';
import { WIZARD_STEP6_TOUR } from '../TourGuide/tourSteps';
import SpendingPhasesTable, { MIN_SPENDING_PHASES, MAX_SPENDING_PHASES } from './SpendingPhasesTable';
import SpendingPhaseEditDrawer from './SpendingPhaseEditDrawer';
import { createDefaultSpendingPhases } from '../../utils/spendingPhaseHelpers';
import './Step5Spending.css';

interface Step5Props {
  initialData?: Partial<AllStepFormData>;
  onNext: (data: Step6FormData) => void;
  onSave: (data: Step6FormData) => void;
  onBack?: () => void;
  lastSaved?: string;
}

function Step5Spending({ initialData, onNext, onSave, onBack, lastSaved }: Readonly<Step5Props>) {
  const {
    register,
    handleSubmit,
    watch,
    control,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<Step6FormData>({
    resolver: zodResolver(Step6Schema),
    defaultValues: {
      spendingPhases: initialData?.spendingPhases?.map((p: SpendingPhase) => ({
        ...p,
        inflationLinked: p.inflationLinked ?? true,
      })) || createDefaultSpendingPhases(),
      oneTimeExpenses: (initialData?.oneTimeExpenses || []).map((e: OneTimeExpense) => ({
        ...e,
        inflationLinked: e.inflationLinked ?? false,
      })),
      recurringExpenses: (initialData?.recurringExpenses || []).map((e: RecurringExpense) => ({
        ...e,
        inflationLinked: e.inflationLinked ?? false,
      })),
    },
  });

  const {
    fields: oneTimeFields,
    append: appendOneTime,
    remove: removeOneTime,
  } = useFieldArray({
    control,
    name: 'oneTimeExpenses',
  });

  const {
    fields: recurringFields,
    append: appendRecurring,
    remove: removeRecurring,
  } = useFieldArray({
    control,
    name: 'recurringExpenses',
  });

  const spendingPhases = watch('spendingPhases');
  const [editingPhaseIndex, setEditingPhaseIndex] = useState<number | null>(null);

  const { append: appendPhase, remove: removePhase } = useFieldArray({
    control,
    name: 'spendingPhases',
  });

  // Register inflation fields explicitly so watch/setValue work with boolean values.
  // React Hook Form needs registration for watch() to return the correct initial value.
  // Re-runs whenever the phase count changes so newly-added phases get registered too.
  const phaseCount = spendingPhases?.length ?? 0;
  useEffect(() => {
    for (let i = 0; i < phaseCount; i++) {
      register(`spendingPhases.${i}.inflationLinked`);
    }
  }, [register, phaseCount]);

  const handleBlur = () => {
    // Always save on blur - isDirty doesn't work reliably with useCurrencyInput
    const data = watch();
    onSave(data);
  };

  const onSubmit = (data: Step6FormData) => {
    onNext(data);
  };

  const handleAddPhase = () => {
    const current = (getValues('spendingPhases') as SpendingPhase[]) || [];
    if (current.length >= MAX_SPENDING_PHASES) return;

    const lastIndex = current.length - 1;
    const last = current[lastIndex];
    let lastEndYear = last?.endYear;
    // If the previous last phase was open-ended, give it a default 10-year
    // span so the new phase can take over as the open-ended tail.
    if (last && lastEndYear == null) {
      lastEndYear = last.startYear + 9;
      setValue(`spendingPhases.${lastIndex}.endYear`, lastEndYear, { shouldValidate: true });
    }
    const newStart = (lastEndYear ?? 0) + 1;

    appendPhase({
      phaseId: crypto.randomUUID(),
      label: 'New Phase',
      startYear: newStart,
      endYear: undefined,
      livingExpenses: 0,
      travelLeisure: 0,
      otherDiscretionary: 0,
      inflationLinked: true,
    });

    const newIndex = current.length;
    setTimeout(() => { setEditingPhaseIndex(newIndex); handleBlur(); }, 0);
  };

  const handleRemovePhase = (index: number) => {
    // Deleting a middle phase used to leave its years covered by no phase — a
    // gap the autosave persisted and the engine projected as zero spending
    // until the user noticed the validation error and fixed the boundary by
    // hand. Hand the removed phase's years to the phase that follows it, so
    // the set stays contiguous through the delete.
    const current = (getValues('spendingPhases') as SpendingPhase[]) || [];
    const removed = current[index];
    removePhase(index);
    if (removed && Number.isFinite(removed.startYear) && index < current.length - 1) {
      setValue(`spendingPhases.${index}.startYear`, removed.startYear, { shouldValidate: true });
    } else if (removed && index === current.length - 1 && index > 0) {
      // Removed the (typically open-ended) last phase: re-open the phase
      // before it, mirroring handleAddPhase's closing of the tail. Otherwise
      // the previous phase's interior endYear would silently become the end
      // of all spending, zeroing every projection year after it.
      setValue(`spendingPhases.${index - 1}.endYear`, undefined, { shouldValidate: true });
    }
    setEditingPhaseIndex(null);
    setTimeout(handleBlur, 0);
  };

  const addOneTimeExpense = () => {
    appendOneTime({
      expenseId: crypto.randomUUID(),
      description: '',
      amount: 0,
      year: 1,
      inflationLinked: true,
    });
    setTimeout(handleBlur, 0);
  };

  const addRecurringExpense = () => {
    appendRecurring({
      expenseId: crypto.randomUUID(),
      description: '',
      amountPerYear: 0,
      startYear: 1,
      endYear: undefined,
      inflationLinked: true,
    });
    setTimeout(handleBlur, 0);
  };

  // Assumptions (Step 8) may not have been visited yet — fall back to the app's standard default.
  // Uses ?? (not ||) since 0% is a deliberate, valid assumption the user can set.
  const isDefaultInflationRate = initialData?.economicAssumptions?.inflationAnnualPct == null;
  const inflationAnnualPct = initialData?.economicAssumptions?.inflationAnnualPct ?? 2.25;

  return (
    <div className="step-container">
      <div className="step-header">
        <div className="step-header-row">
          <h2>Plan your retirement spending</h2>
          <TourButton tour={WIZARD_STEP6_TOUR} variant="text" />
        </div>
        <p>Amounts are in today's pounds and will be adjusted for inflation</p>
      </div>

      <div className="spending-benchmark-card">
        <span className="spending-benchmark-label">PLSA Retirement Living Standards (2024)</span>
        <div className="spending-benchmark-values">
          <div className="spending-benchmark-item">
            <span className="spending-benchmark-level">Minimum</span>
            <span className="spending-benchmark-amount">£14,400 single / £22,400 couple</span>
          </div>
          <div className="spending-benchmark-item">
            <span className="spending-benchmark-level">Moderate</span>
            <span className="spending-benchmark-amount">£31,300 single / £43,100 couple</span>
          </div>
          <div className="spending-benchmark-item">
            <span className="spending-benchmark-level">Comfortable</span>
            <span className="spending-benchmark-amount">£43,100 single / £59,000 couple</span>
          </div>
        </div>
      </div>

      <form onSubmit={(e) => e.preventDefault()} onKeyDown={wizardFormKeyDown} className="step-form">
        {/* Spending Phases */}
        <div className="spending-phases-container">
          <h3 className="section-header">Retirement Spending Phases</h3>
          <p className="help-text">Model up to {MAX_SPENDING_PHASES} phases of retirement spending — click a phase to edit</p>

          <SpendingPhasesTable
            phases={spendingPhases}
            inflationAnnualPct={inflationAnnualPct}
            onRowClick={(index) => setEditingPhaseIndex(index)}
            onAddPhase={handleAddPhase}
            persons={initialData?.persons}
            planAsOfDate={initialData?.planAsOfDate}
            personDeceasedAges={initialData?.personDeceasedAges}
            projectionYears={initialData?.projectionYears}
          />
        </div>

        {editingPhaseIndex !== null && spendingPhases[editingPhaseIndex] && (
          <SpendingPhaseEditDrawer
            key={spendingPhases[editingPhaseIndex].phaseId}
            isOpen
            phaseIndex={editingPhaseIndex}
            phases={spendingPhases}
            minPhases={MIN_SPENDING_PHASES}
            maxPhases={MAX_SPENDING_PHASES}
            control={control}
            register={register}
            setValue={setValue}
            getValues={getValues}
            errors={errors}
            planAsOfDate={initialData?.planAsOfDate}
            inflationAnnualPct={inflationAnnualPct}
            isDefaultInflationRate={isDefaultInflationRate}
            onFieldBlur={handleBlur}
            onClose={() => setEditingPhaseIndex(null)}
            onRemovePhase={() => handleRemovePhase(editingPhaseIndex)}
          />
        )}

        {/* One-Time Expenses */}
        <div className="expenses-section">
          <h3 className="section-header">One-Time Expenses</h3>
          <p className="help-text">Add any one-off expenses during retirement (e.g., new car, home renovation)</p>

          {oneTimeFields.length > 0 ? (
            <div className="expenses-table-wrapper">
              <table className="expenses-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Amount (£)</th>
                    <th>Year</th>
                    <th>Inflation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {oneTimeFields.map((field, index) => (
                    <tr key={field.id}>
                      <td data-label="Description">
                        <input
                          type="text"
                          className={`form-input ${errors.oneTimeExpenses?.[index]?.description ? 'error' : ''}`}
                          {...register(`oneTimeExpenses.${index}.description`)}
                          onBlur={handleBlur}
                          placeholder="e.g., New car"
                        />
                      </td>
                      <td data-label="Amount (£)">
                        <input
                          type="number"
                          className={`form-input ${errors.oneTimeExpenses?.[index]?.amount ? 'error' : ''}`}
                          {...register(`oneTimeExpenses.${index}.amount`, { valueAsNumber: true })}
                          onBlur={handleBlur}
                        />
                      </td>
                      <td data-label="Year">
                        <div className="year-input-row">
                          <input
                            type="number"
                            min="1"
                            className={`form-input form-input-year ${errors.oneTimeExpenses?.[index]?.year ? 'error' : ''}`}
                            {...register(`oneTimeExpenses.${index}.year`, { valueAsNumber: true })}
                            onBlur={handleBlur}
                          />
                          {initialData?.planAsOfDate && watch(`oneTimeExpenses.${index}.year`) && (
                            <span className="field-context-hint field-context-hint--inline">{planYearToCalendar(watch(`oneTimeExpenses.${index}.year`), initialData.planAsOfDate)}</span>
                          )}
                        </div>
                      </td>
                      <td data-label="Inflation" className="inflation-cell">
                        <div className="inflation-segmented inflation-segmented--compact" role="group" aria-label="Inflation adjustment">
                          <button
                            type="button"
                            className={`inflation-seg-btn inflation-seg-btn--sm ${watch(`oneTimeExpenses.${index}.inflationLinked`) === false ? 'inflation-seg-btn--fixed-active' : 'inflation-seg-btn--inactive'}`}
                            onClick={() => { setValue(`oneTimeExpenses.${index}.inflationLinked`, false); handleBlur(); }}
                          >Fixed</button>
                          <button
                            type="button"
                            className={`inflation-seg-btn inflation-seg-btn--sm ${watch(`oneTimeExpenses.${index}.inflationLinked`) !== false ? 'inflation-seg-btn--inflation-active' : 'inflation-seg-btn--inactive'}`}
                            onClick={() => { setValue(`oneTimeExpenses.${index}.inflationLinked`, true); handleBlur(); }}
                          >Inflation</button>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => { removeOneTime(index); setTimeout(handleBlur, 0); }}
                          className="btn-icon btn-delete"
                          aria-label="Delete expense"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No one-time expenses added</p>
          )}

          <button type="button" onClick={addOneTimeExpense} className="btn btn-secondary add-expense-btn">
            + Add One-Time Expense
          </button>
        </div>

        {/* Recurring Expenses */}
        <div className="expenses-section">
          <h3 className="section-header">Recurring Annual Expenses</h3>
          <p className="help-text">Add expenses that recur every year (e.g., helping children, charity giving)</p>

          {recurringFields.length > 0 ? (
            <div className="expenses-table-wrapper">
              <table className="expenses-table">
                <thead>
                  <tr>
                    <th>Description</th>
                    <th>Amount (£/year)</th>
                    <th>Start Year</th>
                    <th>End Year (optional)</th>
                    <th>Inflation</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringFields.map((field, index) => (
                    <tr key={field.id}>
                      <td data-label="Description">
                        <input
                          type="text"
                          className={`form-input ${errors.recurringExpenses?.[index]?.description ? 'error' : ''}`}
                          {...register(`recurringExpenses.${index}.description`)}
                          onBlur={handleBlur}
                          placeholder="e.g., Help children"
                        />
                      </td>
                      <td data-label="Amount (£)">
                        <input
                          type="number"
                          className={`form-input ${errors.recurringExpenses?.[index]?.amountPerYear ? 'error' : ''}`}
                          {...register(`recurringExpenses.${index}.amountPerYear`, { valueAsNumber: true })}
                          onBlur={handleBlur}
                        />
                      </td>
                      <td data-label="Start Year">
                        <input
                          type="number"
                          min="1"
                          className={`form-input ${errors.recurringExpenses?.[index]?.startYear ? 'error' : ''}`}
                          {...register(`recurringExpenses.${index}.startYear`, { valueAsNumber: true })}
                          onBlur={handleBlur}
                        />
                        {initialData?.planAsOfDate && watch(`recurringExpenses.${index}.startYear`) && (
                          <span className="field-context-hint">{planYearToCalendar(watch(`recurringExpenses.${index}.startYear`), initialData.planAsOfDate)}</span>
                        )}
                      </td>
                      <td data-label="End Year">
                        <input
                          type="number"
                          min="1"
                          className={`form-input ${errors.recurringExpenses?.[index]?.endYear ? 'error' : ''}`}
                          {...register(`recurringExpenses.${index}.endYear`, { valueAsNumber: true })}
                          onBlur={handleBlur}
                          placeholder="Leave blank for all years"
                        />
                        {initialData?.planAsOfDate && (() => {
                          const yr = watch(`recurringExpenses.${index}.endYear`);
                          return yr ? <span className="field-context-hint">{planYearToCalendar(yr, initialData.planAsOfDate)}</span> : null;
                        })()}
                      </td>
                      <td data-label="Inflation" className="inflation-cell">
                        <div className="inflation-segmented inflation-segmented--compact" role="group" aria-label="Inflation adjustment">
                          <button
                            type="button"
                            className={`inflation-seg-btn inflation-seg-btn--sm ${watch(`recurringExpenses.${index}.inflationLinked`) === false ? 'inflation-seg-btn--fixed-active' : 'inflation-seg-btn--inactive'}`}
                            onClick={() => { setValue(`recurringExpenses.${index}.inflationLinked`, false); handleBlur(); }}
                          >Fixed</button>
                          <button
                            type="button"
                            className={`inflation-seg-btn inflation-seg-btn--sm ${watch(`recurringExpenses.${index}.inflationLinked`) !== false ? 'inflation-seg-btn--inflation-active' : 'inflation-seg-btn--inactive'}`}
                            onClick={() => { setValue(`recurringExpenses.${index}.inflationLinked`, true); handleBlur(); }}
                          >Inflation</button>
                        </div>
                      </td>
                      <td>
                        <button
                          type="button"
                          onClick={() => { removeRecurring(index); setTimeout(handleBlur, 0); }}
                          className="btn-icon btn-delete"
                          aria-label="Delete expense"
                        >
                          🗑️
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <p className="empty-state">No recurring expenses added</p>
          )}

          <button type="button" onClick={addRecurringExpense} className="btn btn-secondary add-expense-btn">
            + Add Recurring Expense
          </button>
        </div>

        {/* Important Notes */}
        <div className="info-box">
          <h4>Important Notes</h4>
          <ul>
            <li>💡 All amounts should be NET (after-tax) spending in today's prices</li>
            <li>💡 We'll calculate the GROSS withdrawals needed and adjust for inflation automatically</li>
            <li>💡 State pension income is included automatically based on your details from Step 2</li>
          </ul>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          {onBack && (
            <button type="button" className="btn btn-secondary" onClick={onBack}>
              Back
            </button>
          )}
          <button type="button" className="btn btn-primary" onClick={() => handleSubmit(onSubmit)()}>
            Next: Later Life & Estate
          </button>
          {lastSaved && (
            <span className="save-indicator">
              Saved
            </span>
          )}
        </div>
      </form>
    </div>
  );
}

export default Step5Spending;
